<template>
  <div class="full-screen-overlay bg-primary opacity-25">
    <div class="spinner-border" style="width: 10rem; height: 10rem" role="status">
      <span class="visually-hidden">Loading... </span>
    </div>
  </div>
  <div class="alert alert-warning mt-2" role="alert" v-if="state.warnings.value.length">
    <div class="mb-2" v-for="w in state.warnings.value">
      <h3 v-if="w.message">{{ w.message }}</h3>
    </div>
  </div>
  <div class="alert alert-danger mt-2" role="alert" v-if="state.errors.value.length">
    <div class="mb-2" v-for="e in state.errors.value">
      <h3 v-if="e.message">{{ e.message }}</h3>
      <p v-else>{{ e }}</p>
      <h6 v-if="e.url">{{ e.url }}</h6>
      <pre v-if="e.stack" class="mt-3">
        {{ e.stack }}
      </pre>
      <ul v-if="e.stackTrace" class="mt-3 mb-5">
        <li v-for="line in e.stackTrace.split('\n')">{{ line }}</li>
      </ul>
      <ul v-if="e.invalidProperties">
        <li v-for="p in e.invalidProperties">{{ p.errorMessage }}</li>
      </ul>
    </div>
  </div>
  <div class="app-toast" :style="{ bottom: toastBottom }" v-if="state.toast.value" role="alert" aria-live="assertive">
    <!-- keyed, so a new toast replaying the same message still pops in -->
    <div
      :key="state.toast.value.id"
      class="app-toast-body d-flex align-items-center gap-3 fs-5 fw-semibold"
      :class="`text-bg-${state.toast.value.type}`"
    >
      <span class="app-toast-icon" aria-hidden="true">{{ TOAST_ICONS[state.toast.value.type] ?? "ℹ️" }}</span>
      <div class="flex-grow-1">{{ state.toast.value.message }}</div>
      <button
        type="button"
        class="btn-close"
        :class="{ 'btn-close-white': state.toast.value.type !== 'warning' }"
        aria-label="Zapri"
        @click="hideToast"
      ></button>
    </div>
  </div>
</template>

<script setup>
import { nextTick, onMounted, ref, watch } from "vue"
import { useRouter } from "vue-router"
import { useNotification, clearNotifications, hideToast } from "./Notification"

// the component half of components/Notification.js, which stays a plain module: the notification
// STORE (state + setters) is imported by ~35 files that have nothing to do with this markup
defineOptions({ name: "Notification" })

const props = defineProps({
  noRouter: {
    type: Boolean,
    default: false,
  },
})

const { state } = useNotification()

const TOAST_ICONS = { success: "✅", warning: "⚠️", danger: "⛔" }

// keep the toast just above a sticky action row (.sticky-actions) while that row is pinned to the bottom of the screen
const toastBottom = ref(undefined)
const placeToast = () => {
  let covered = 0
  for (const bar of document.querySelectorAll(".sticky-actions")) {
    const rect = bar.getBoundingClientRect()
    if (rect.height > 0 && rect.bottom >= window.innerHeight - 1) {
      covered = Math.max(covered, window.innerHeight - rect.top)
    }
  }
  toastBottom.value = covered > 0 ? `calc(${Math.round(covered)}px + 0.5rem)` : undefined
}
watch(
  () => state.toast.value,
  (toast) => {
    if (toast) {
      nextTick(placeToast)
      window.addEventListener("scroll", placeToast, { passive: true })
      window.addEventListener("resize", placeToast, { passive: true })
    } else {
      window.removeEventListener("scroll", placeToast)
      window.removeEventListener("resize", placeToast)
    }
  },
)
const router = useRouter()

if (!props.noRouter) {
  // clear warnings and errors on route change (a success toast survives it)
  if (router) {
    router.afterEach(() => clearNotifications())
  }
}

onMounted(() => {
  if (state.loading.value) {
    document.body.classList.add("show-loader")
  } else {
    document.body.classList.remove("show-loader")
  }
})
</script>

<style scoped>
/* bottom of the screen: where the thumb is on a phone, and clear of the top menu */
.app-toast {
  position: fixed;
  left: 0.75rem;
  right: 0.75rem;
  bottom: calc(0.75rem + env(safe-area-inset-bottom));
  max-width: 40rem;
  margin: 0 auto;
  z-index: 1090; /* above Bootstrap's sticky/fixed content and modals' backdrop */
}

/* solid colour, heavy shadow and a short pop-in, so it can't be missed on a busy phone screen */
.app-toast-body {
  padding: 1rem 1.25rem;
  border-radius: 0.75rem;
  border: 2px solid rgba(0, 0, 0, 0.25);
  box-shadow:
    0 0.75rem 2rem rgba(0, 0, 0, 0.35),
    0 0 0 0.25rem rgba(255, 255, 255, 0.6);
  animation: app-toast-in 0.35s cubic-bezier(0.2, 1.4, 0.4, 1);
}

.app-toast-icon {
  font-size: 1.75rem;
  line-height: 1;
}

@keyframes app-toast-in {
  from {
    opacity: 0;
    transform: translateY(1.5rem) scale(0.95);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .app-toast-body {
    animation: none;
  }
}
</style>
